<template>
    <Dialog :visible="visible" class="dialog-container custom-dialog" modal :style="{ width: '30rem' }" :closable="false">
        <template #header>
            <div class="header-container">
                <span class="title-dialog">{{ header }}</span>
            </div>
        </template>
        <div class="form-container mt-3">
            <FormKit type="form"
                id="approvedVersionForm"
                @submit="submitForm"
                :actions="false"
                incomplete-message="Désolé, tous les champs ne sont pas remplis correctement."
                #default="{ state: { valid } }"
            >
                <div :class="['form-field', {'invalid-field': !formData.version}]">
                    <FormKit type="text"
                        name="version"
                        label="Version *"
                        v-model="formData.version"
                        :validation-rules="{ validateNotEmptyText }"
                        validation="required|validateNotEmptyText"
                        :validation-messages="{
                            required: requiredErrorMessage,
                            validateNotEmptyText: requiredErrorMessage,
                        }"
                    />
                </div>
                <div :class="['form-field', {'invalid-field': !formData.os}]">
                    <FormKit type="dropdown"
                        name="os"
                        v-model="formData.os"
                        label="Platforme *"
                        :options="mobileDevices"
                        validation="required|validateNotEmptyText"
                        :validation-messages="{
                            required: requiredErrorMessage,
                            validateNotEmptyText: requiredErrorMessage,
                        }"
                    />
                </div>
                <div :class="['form-field', {'invalid-field': !formData.domain}]">
                    <FormKit type="dropdown"
                        name="domain"
                        v-model="formData.domain"
                        label="Domaine *"
                        :options="domains"
                        validation="required|validateNotEmptyText"
                        :validation-messages="{
                            required: requiredErrorMessage,
                            validateNotEmptyText: requiredErrorMessage,
                        }"
                    />
                </div>

                <div class="button-group flex justify-between w-full px-4">
                    <Button type="submit"
                            :disabled="!valid || isSubmitting"
                            label="Ajouter"
                            :style="{
                                backgroundColor: (!valid || isSubmitting) ? 'gray' : '#007bff',
                                border: (!valid || isSubmitting) ? '1px solid gray' : '1px solid #007bff'
                            }"
                            class="mr-3 rounded focus:outline-none focus:shadow-outline"
                    />
                    <Button type="button" label="Retour" class="p-button-danger" @click="closeDialog" />
                </div>
            </FormKit>
        </div>
    </Dialog>
</template>

<script lang="ts" setup>
import {ref, reactive, watch, defineEmits, defineProps} from 'vue';
import {ApprovedVersionService} from "@/_services/securite/approvedVersion/ApprovedVersionService";
import {IApprovedVersionFormSubmittedEvent, IPostApprovedVersion} from "@/models/ApprovedVersion/IApprovedVersion";

const props = defineProps({
    visible: {
        type: Boolean,
        required: true
    },
});
const emit = defineEmits<{
    (event: 'update:visible', value: boolean): void;
    (event: 'update:form-submitted', payload: IApprovedVersionFormSubmittedEvent): void;
}>();

const requiredErrorMessage = 'Ce champ est obligatoire.';
const header = ref<string>('AJOUTER');
const isSubmitting = ref(false);
const formData = reactive<IPostApprovedVersion>({
    version: '',
    os: '',
    domain: '',
});
const mobileDevices = [
    { label: 'ANDROID', value: 'ANDROID' },
    { label: 'iOS', value: 'iOS' }
];
const domains = [
    { label: 'gifteo', value: 'gifteo' },
    { label: 'club-employes', value: 'club-employes' }
];

const validateNotEmptyText = (node) => {
    let value = node.value;
    const regex = new RegExp(/\S/);

    return regex.test(value);
}

const submitForm = async (formData: IPostApprovedVersion, formNode) => {
    try {
        isSubmitting.value = true;
        const axiosResponse = await ApprovedVersionService.createApprovedVersion(formData);
        if (axiosResponse?.response?.data?.hasError === true) {
            throw new Error(axiosResponse?.response?.data?.errors[0].message || "Une erreur est survenue. Veuillez réessayer.");
        }
        
        emit('update:form-submitted', {
            status: 'success',
            message: 'Version ajoutée avec succès',
        });
    } catch (axiosError: any) {
        emit('update:form-submitted', {
            status: 'error',
            message: axiosError,
        });
    } finally {
       isSubmitting.value = false;
    }
};

const closeDialog = (): void => {
    emit('update:visible', false);
    resetFormData();
};

const resetFormData = (): void => {
    formData.version = '';
    formData.os = '';
    formData.domain = '';
};

watch(() => props.visible, (newVal) => {
    if (!newVal) {
        resetFormData();
    }
});

</script>

<style scoped>
.dialog-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
}

.custom-dialog {
    width: 100%;
    max-width: 600px;
    padding: 20px;
    box-sizing: border-box;
}

.form-container {
    width: 100%;
}

.form-field, .button-group {
    width: 100%;
    margin-bottom: 10px;
}

input, select, textarea, .button-group button {
    width: 100%;
    padding: 10px;
    box-sizing: border-box;
}

.title-dialog {
    font-size: 20px;
    text-transform: uppercase;
    color: #0A3382;
    position: relative;
}

.title-dialog:after {
    content: "";
    background: #f5a22e;
    width: 35px;
    height: 3px;
    position: absolute;
    bottom: -5px;
    left: 0;
    display: block;
}
</style>
